import React from 'react'
import { Keyframes } from '../Keyframes'
import { motion } from 'framer-motion'

export interface AnimatedCheckMarkProps {
  fillColor?: string
  fillLightColor?: string
  fillDarkColor?: string
  checkColor?: string
  size?: number
  delay?: number
}

const StaticGradientCheckMark = (props: AnimatedCheckMarkProps) => {
  const { fillLightColor, fillDarkColor, checkColor = '#fff' } = props
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 52 52'
      style={{
        borderRadius: '50%',
        display: 'block',
        strokeWidth: 5,
        stroke: checkColor,
      }}
    >
      <circle
        cx='26'
        cy='26'
        r='27'
        fill='none'
        style={{
          fill: 'url(#paint0_linear_843_863)',
          strokeWidth: 0,
        }}
      />
      <path
        fill='none'
        d='M14.1 27.2l7.1 7.2 16.7-16.8'
        style={{
          animation: `stroke 0s cubic-bezier(0.65, 0, 0.45, 1) 0s forwards`,
        }}
      />
      <linearGradient
        id='paint0_linear_843_863'
        x1='61'
        y1='17.5'
        x2='61'
        y2='88.4565'
        gradientUnits='userSpaceOnUse'
      >
        <stop stopColor={fillLightColor} />
        <stop offset='1' stopColor={fillDarkColor} />
      </linearGradient>
    </svg>
  )
}

const AnimatedSolidCheckMark = (props: AnimatedCheckMarkProps) => {
  const {
    delay = 0,
    size = 102,
    fillColor = '#7ac142',
    checkColor = '#fff',
  } = props
  return (
    <>
      <Keyframes
        name='scale'
        _0={{ transform: 'none' }}
        _50={{ transform: 'scale3d(1.1, 1.1, 1)' }}
        _100={{ transform: 'none' }}
      />
      <Keyframes name='stroke' _100={{ strokeDashoffset: 0 }} />
      <Keyframes
        name='fill'
        _100={{
          boxShadow: `inset 0px 0px 0px ${(size || 102) / 2}px ${fillColor}`,
        }}
      />
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 52 52'
        style={{
          borderRadius: '50%',
          display: 'block',
          strokeWidth: 5,
          stroke: checkColor,
          strokeMiterlimit: '10',
          boxShadow: `inset 0px 0px 0px ${fillColor}`,
          animation: `fill 0.3s ease-in-out ${
            delay + 0.4
          }s forwards, scale 0.3s ease-in-out ${delay + 0.8}s both`,
        }}
      >
        <circle
          cx='26'
          cy='26'
          r='25'
          fill='none'
          style={{
            strokeDasharray: '166',
            strokeDashoffset: 166,
            strokeWidth: 2,
            strokeMiterlimit: '10',
            stroke: fillColor,
            fill: 'none',
            animation: `stroke 0.5s cubic-bezier(0.65, 0, 0.45, 1) ${delay}s forwards`,
          }}
        />
        <path
          fill='none'
          d='M14.1 27.2l7.1 7.2 16.7-16.8'
          style={{
            transformOrigin: '50% 50%',
            strokeDasharray: '48',
            strokeDashoffset: 48,
            animation: `stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) ${
              delay + 0.8
            }s forwards`,
          }}
        />
      </svg>
    </>
  )
}

const AnimatedGradientCheckMark = (props: AnimatedCheckMarkProps) => {
  const delay = props.delay || 0
  return (
    <>
      <AnimatedSolidCheckMark {...props} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: { duration: 0.3, delay: delay + 1 },
        }}
        style={{
          position: 'absolute',
          width: props.size,
          height: props.size,
          top: 0,
          left: 0,
        }}
      >
        <StaticGradientCheckMark {...props} />
      </motion.div>
    </>
  )
}

const AnimatedCheckMark = (props: AnimatedCheckMarkProps) => {
  return (
    <div
      style={{
        display: 'flex',
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        width: props.size || 102,
      }}
    >
      {props.fillLightColor && props.fillDarkColor ? (
        <AnimatedGradientCheckMark {...props} />
      ) : (
        <AnimatedSolidCheckMark {...props} />
      )}
    </div>
  )
}

export default AnimatedCheckMark
